import { computed, onUnmounted, ref } from 'vue'
import { useErrorAssistStore } from '@/stores/errorAssistStore'
import type { _AxiosError } from '@/stores/api'
import type { ChatMessageAdditionalData } from '@/types/conversation.type'

const POLL_INTERVAL_MS = 1000
const POLL_TIMEOUT_MS = 3 * 60 * 1000

// BE 側で障害が起きるとエラーのたびに会話が積まれて queue が溢れうるため、新規会話の
// 作成は 30 秒に 1 回までに制限する（BE チームからの依頼）。成否を問わず試行時刻で数える
const DIAGNOSIS_COOLDOWN_MS = 30 * 1000

// 会話 polling は通常 useConversationPolling に任せるが、診断は間隔・タイムアウト・
// 完了判定・エラー処理のすべてがそれと異なるため、専用の実行管理を持つ
export function useErrorAssistDiagnosis() {
  const errorAssistStore = useErrorAssistStore()

  let pollTimeoutId: number | null = null
  let pollStartedAt = 0
  let lastConversationRequestedAt = 0

  // 診断の会話 id。公開はシートを開く合図になるため、質問の post 完了まで待つ。
  // 先に公開すると follow-up が質問より先に届きうる
  const conversationId = ref<number | null>(null)

  // 診断の終わり（成功・失敗とも）。回答の中身は store の answer が持つので、UI の状態は
  // このフラグと answer の有無から導出できる
  const diagnosisDone = ref(false)

  const isAnswerReady = computed(() => diagnosisDone.value && errorAssistStore.answer !== '')
  const isDiagnosisFailed = computed(() => diagnosisDone.value && errorAssistStore.answer === '')
  const isDiagnosing = computed(() => !diagnosisDone.value)

  function stopPolling() {
    if (pollTimeoutId !== null) {
      window.clearTimeout(pollTimeoutId)
      pollTimeoutId = null
    }
  }

  onUnmounted(stopPolling)

  function poll(id: number) {
    pollTimeoutId = window.setTimeout(() => {
      if (Date.now() - pollStartedAt >= POLL_TIMEOUT_MS) {
        diagnosisDone.value = true
        return
      }
      const messages = errorAssistStore.messages
      const lastMessageId = messages[messages.length - 1]?.id
      errorAssistStore
        .fetchActiveTx(id, lastMessageId)
        .then(() => {
          // activeTx が消えたら run は終わっている。回答が空のまま終わることもある
          // （server 側で run が失敗した場合など）ので、answer の有無は完了後の判定に回す
          if (errorAssistStore.activeTx === null) {
            diagnosisDone.value = true
            return
          }
          poll(id)
        })
        .catch((err: _AxiosError) => {
          // 401 がここに届くのは interceptor の refresh を経ても回復しなかったとき
          // （= セッション切れで logout 済み）。続けても毎秒同じ失敗を繰り返すだけ
          if (err.response?.status === 401) {
            diagnosisDone.value = true
            return
          }
          // API エラー直後に走る機能なので瞬断は起きやすい。1 回の失敗で診断を
          // 打ち切らず、打ち切りは上の 3 分タイムアウトに任せる
          poll(id)
        })
    }, POLL_INTERVAL_MS)
  }

  function isDiagnosisThrottled(): boolean {
    return Date.now() - lastConversationRequestedAt < DIAGNOSIS_COOLDOWN_MS
  }

  function startDiagnosis(
    question: string,
    title: string,
    additionalData: ChatMessageAdditionalData,
  ) {
    stopPolling()
    errorAssistStore.resetDiagnosis()
    conversationId.value = null
    diagnosisDone.value = false
    pollStartedAt = Date.now()
    lastConversationRequestedAt = pollStartedAt

    errorAssistStore
      .createConversation(title)
      .then((id) =>
        errorAssistStore.postChatMessage(question, id, additionalData).then(() => {
          conversationId.value = id
          poll(id)
        }),
      )
      .catch((error) => {
        console.error('Failed to start the AI diagnosis:', error)
        diagnosisDone.value = true
      })
  }

  function sendFollowUp(text: string, additionalData: ChatMessageAdditionalData) {
    const id = conversationId.value
    if (id === null) return Promise.resolve()
    stopPolling()
    return errorAssistStore.postChatMessage(text, id, additionalData).then(() => {
      diagnosisDone.value = false
      pollStartedAt = Date.now()
      poll(id)
    })
  }

  function resetDiagnosis() {
    stopPolling()
    errorAssistStore.resetDiagnosis()
    conversationId.value = null
    diagnosisDone.value = false
  }

  return {
    conversationId,
    isAnswerReady,
    isDiagnosisFailed,
    isDiagnosing,
    isDiagnosisThrottled,
    startDiagnosis,
    sendFollowUp,
    resetDiagnosis,
  }
}
